import React, {useCallback, useEffect, useRef, useState} from 'react';
import {parseQuestionCommand, findNumberedQuestion, recognitionCommand, incompleteQuestionCommand} from './voiceCommands';

import VoiceAssistantOverlay from './VoiceAssistantOverlay';

const IDLE = 'Ready. Say “Hey Instil, twelve” or “Hey Instil, question twelve.”';
function preference(key) {
    try { return window.sessionStorage.getItem(key) === 'on'; } catch { return false; }
}
function remember(key, enabled) {
    try { window.sessionStorage.setItem(key, enabled ? 'on' : 'off'); } catch { /* Voice still works on this page. */ }
}

function savedLanguage(key) {
    try { const value=window.sessionStorage.getItem(`${key}:language`); return ['en-IN','en-US','en-GB'].includes(value)?value:'en-IN'; } catch { return 'en-IN'; }
}

export default function VoiceQuestionSearch({questions, onOpen, disabled=false, userId='student'}) {
    const preferenceKey = `instil-iq-hands-free:${userId}`;
    const [enabled, setEnabled] = useState(() => preference(preferenceKey));
    const [listening, setListening] = useState(false);
    const [awake, setAwake] = useState(false);
    const [visible, setVisible] = useState(() => !document.hidden);
    const [message, setMessage] = useState('Enable hands-free once, then just say “Hey Instil” and a question number.');
    const [transcript, setTranscript] = useState('');
    const [typed, setTyped] = useState('');
    const [language, setLanguage] = useState(() => savedLanguage(preferenceKey));
    const [phase, setPhase] = useState('idle');
    const [speaking, setSpeaking] = useState(false);
    const [choices, setChoices] = useState([]);
    const chooseNumber = useRef(() => {});
    const correctNumber = useCallback(value => chooseNumber.current(value), []);
    const cancelCommand = useRef(() => {});
    const dismiss = useCallback(() => cancelCommand.current(), []);
    const latest = useRef({questions, onOpen, disabled});
    latest.current = {questions, onOpen, disabled};
    const supported = typeof window !== 'undefined' && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

    useEffect(() => {
        const changed = () => setVisible(!document.hidden);
        document.addEventListener('visibilitychange', changed);
        return () => document.removeEventListener('visibilitychange', changed);
    }, []);

    useEffect(() => {
        setListening(false);
        setAwake(false);
        setPhase('idle');
        setChoices([]);
        setSpeaking(false);
        if (!enabled || !supported) return;
        if (!visible || disabled) {
            setMessage(disabled ? 'Voice is paused while your answer saves.' : 'Voice is paused while this tab is hidden.');
            return;
        }
        let disposed = false;
        let recognition = null;
        let restartTimer, settleTimer, flushTimer, wakeTimer, feedbackTimer;
        let armed = false;
        let command = '';
        let finalCommand = false;
        let ambiguous = false;
        let candidates = [];
        let needsConfirmation = false;
        let notice = '';
        let emptyEnds = 0;
        const detach = () => {
            if (!recognition) return;
            const current = recognition;
            recognition = null;
            current.onresult = current.onend = current.onerror = current.onstart = current.onspeechstart = current.onspeechend = null;
            try { current.abort(); } catch { /* Already stopped. */ }
        };
        const resetCommand = () => {
            armed = false;
            command = '';
            finalCommand = false;
            ambiguous = false;
            candidates = [];
            needsConfirmation = false;
            clearTimeout(wakeTimer);
            setAwake(false);
            setSpeaking(false);
        };
        const fail = text => {
            remember(preferenceKey, false);
            setEnabled(false);
            setMessage(text);
        };
        const offerRetry = (text, numbers=[]) => {
            resetCommand();
            armed = true; // Accept a repeated number without another wake phrase.
            setAwake(true);
            notice = text;
            setMessage(text);
            setPhase(numbers.length ? 'confirm' : 'error');
            setChoices(numbers.slice(0,3).map(number => {
                const match=findNumberedQuestion(latest.current.questions,number);
                return {number,label:match.question?.topic || (match.error ? 'Unavailable or duplicated' : 'Practice question')};
            }));
            clearTimeout(feedbackTimer);
            wakeTimer = setTimeout(() => {
                resetCommand();
                notice = 'Say “Hey Instil” to try again, or enter the number below.';setMessage(notice);
                endCycle();
            }, 15000);
        };
        const openNumber = number => {
            if(latest.current.disabled || document.hidden)return;
            const match=findNumberedQuestion(latest.current.questions,number);
            if(match.error){offerRetry(`${match.error} You can say another number or enter it below.`);return;}
            resetCommand();setChoices([]);
            try {
                const opened=latest.current.onOpen(match.question);
                if(opened===false){offerRetry('Stayed on this question. Say another number when ready.');return;}
                notice=`Opened Q${number} · ${match.question.topic || 'General practice'}. Say “Hey Instil” for another question.`;
                setPhase('success');setMessage(notice);
                clearTimeout(feedbackTimer);
                feedbackTimer=setTimeout(()=>setPhase('idle'),2200);
            } catch {
                offerRetry('Couldn’t open that question. Try its number again or use the question card.');
            }
        };
        const finishCommand = () => {
            if (!armed || (!ambiguous && incompleteQuestionCommand(command))) return;
            const number=parseQuestionCommand(command,{requireWake:false});
            const options=[...new Set([...candidates,...(number===null?[]:[number])])];
            if(ambiguous || needsConfirmation || !finalCommand) {
                offerRetry(options.length ? 'Please check the number I heard. Choose it below, or say the number again.' : 'I couldn’t catch the number clearly. Say just the number again, or enter it below.',options);
            } else if(number===null) {
                offerRetry('I couldn’t match that request. Say just the question number again, or enter it below.');
            } else openNumber(number);
        };
        const endCycle = () => {
            if (disposed) return;
            clearTimeout(settleTimer);settleTimer=null;
            clearTimeout(flushTimer);
            clearTimeout(restartTimer);
            detach();
            setListening(false);
            if (!latest.current.disabled && !document.hidden) finishCommand();
            // Quiet sessions can end normally in browsers. Back off to avoid a
            // tight start/end loop; permission/network errors disable the mode.
            restartTimer = setTimeout(startCycle, armed ? 160 : emptyEnds < 2 ? 240 : Math.min(5000, 400 * (1 + emptyEnds)));
        };
        chooseNumber.current = value => {
            if(disposed || latest.current.disabled || document.hidden)return;
            const number=parseQuestionCommand(value,{requireWake:false});
            if(number===null){setMessage('Enter a number from 1 to 9999.');return;}
            clearTimeout(settleTimer);settleTimer=null;clearTimeout(flushTimer);clearTimeout(restartTimer);
            detach();openNumber(number);
            restartTimer=setTimeout(startCycle,240);
        };
        cancelCommand.current = () => {
            resetCommand();
            clearTimeout(feedbackTimer);
            setPhase('idle');
            setTranscript('');setChoices([]);
            notice = '';
            setMessage(IDLE);
            endCycle();
        };
        function startCycle() {
            if (disposed || document.hidden || latest.current.disabled) return;
            let current;
            try {
                const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
                current = new Recognition();
            } catch {
                fail('Voice is unavailable right now. Type a number instead.');
                return;
            }
            recognition = current;
            const carried = armed ? command : '';
            const wasArmed = armed;
            current.lang = language;
            current.continuous = true;
            current.interimResults = true;
            current.maxAlternatives = 3;
            current.onstart = () => {
                if (disposed || recognition!==current) return;
                setListening(true);
                setMessage(notice || (armed ? 'I’m listening. Say the question number, then pause.' : IDLE));
            };
            current.onspeechstart = () => { if(disposed || recognition!==current)return;clearTimeout(settleTimer);settleTimer=null; if(armed){setSpeaking(true);setPhase('listening');} };
            current.onresult = event => {
                if (disposed || recognition!==current || latest.current.disabled || document.hidden) return;
                emptyEnds = 0;
                // Use bounded alternative hypotheses, retaining the primary
                // number whenever it is valid. Interim guesses are replaced.
                const heard = recognitionCommand(event.results, {armed, carried});
                if (!heard) return;
                command = heard.command;
                ambiguous = Boolean(heard.ambiguous);
                candidates = heard.options || (heard.number===null ? [] : [heard.number]);
                needsConfirmation = Boolean(heard.needsConfirmation);
                const last = event.results[event.results.length - 1];
                finalCommand = Boolean(last?.isFinal);
                const confidence=last?.[0]?.confidence;
                if(confidence>0 && confidence<0.55)needsConfirmation=true;
                setTranscript(wasArmed && !/\bhey\b/i.test(heard.text) ? `Hey Instil … ${heard.text}` : heard.text);
                clearTimeout(feedbackTimer);
                setPhase('listening');setChoices([]);
                setSpeaking(!finalCommand);
                if (!armed) {
                    armed = true;
                    setAwake(true);
                    notice = '';
                }
                // The limit is silence since the last result, not ten seconds
                // since waking, so a slower speaker is not cut off mid-request.
                clearTimeout(wakeTimer);
                wakeTimer = setTimeout(() => {
                    resetCommand();
                    notice = 'No complete question number heard. Say “Hey Instil” to try again.';
                    setMessage(notice);
                    setPhase('error');
                    feedbackTimer = setTimeout(() => setPhase('idle'), 6000);
                    endCycle();
                }, 10000);
                setMessage(command ? 'Listening to your number… pause to open the question.' : 'Yes? Say a question number.');
                clearTimeout(settleTimer);settleTimer=null;
                // Use a final result after a brief pause. Never navigate on an
                // interim "twenty" while the student is still saying "four".
                if (command && finalCommand && !incompleteQuestionCommand(command)) settleTimer = setTimeout(endCycle, 850);
            };
            current.onspeechend = () => {
                if(disposed || recognition!==current)return;
                setSpeaking(false);
                if (!armed || incompleteQuestionCommand(command)) return;
                setPhase('processing');
                clearTimeout(settleTimer);settleTimer=null;
                clearTimeout(flushTimer);
                // stop() asks the browser for its final transcript; abort() would
                // discard it. Wait for onend, with a bounded fallback for engines
                // which fail to send onend after stopping.
                flushTimer = setTimeout(endCycle, 6000);
                try { current.stop(); } catch { /* May already be ending. */ }
            };
            current.onend = () => {
                if(disposed || recognition!==current)return;
                emptyEnds++;clearTimeout(flushTimer);detach();setListening(false);
                // Chrome may end a service session between words. Keep the wake
                // and prefix; allow the next session to finish the same request.
                if(armed && command && !incompleteQuestionCommand(command) && !settleTimer)settleTimer=setTimeout(endCycle,850);
                clearTimeout(restartTimer);
                restartTimer=setTimeout(startCycle,armed?160:Math.min(5000,400*(1+emptyEnds)));
            };
            current.onerror = event => {
                if(disposed || recognition!==current)return;
                if (event.error === 'no-speech') return; // onend resumes wake listening.
                const messages = {
                    'not-allowed':'Microphone permission was denied. Allow it in site settings, then enable hands-free.',
                    'service-not-allowed':'Your browser disabled voice recognition. Type a number instead.',
                    'audio-capture':'No working microphone was found. Check it, then enable hands-free again.',
                    'network':'The speech service could not connect. Check your connection, then enable hands-free again.',
                    'language-not-supported':'This language is unavailable. Choose another English option.'
                };
                detach();
                fail(messages[event.error] || 'Voice stopped. Enable hands-free again or type a number.');
            };
            try { current.start(); }
            catch { fail('Your browser couldn’t start the microphone automatically. Click Enable hands-free to try again.'); }
        }
        if (window.isSecureContext === false) fail('Voice needs HTTPS. You can type a question number below.');
        else startCycle();
        return () => {
            disposed = true;
            [restartTimer, settleTimer, flushTimer, wakeTimer, feedbackTimer].forEach(clearTimeout);
            chooseNumber.current = () => {};cancelCommand.current = () => {};
            detach();
        };
    }, [enabled, supported, visible, disabled, language, preferenceKey]);

    const toggle = () => {
        remember(preferenceKey, !enabled);
        setEnabled(!enabled);
        setMessage(enabled ? 'Hands-free is off. Microphone stopped.' : 'Starting microphone…');
    };
    const disableVoice = () => {
        remember(preferenceKey, false);setEnabled(false);setMessage('Hands-free is off. Microphone stopped.');
    };
    const changeLanguage = event => {
        const value=event.target.value;setLanguage(value);
        try { window.sessionStorage.setItem(`${preferenceKey}:language`, value); } catch { /* Use it for this page. */ }
    };
    const openTyped = event => {
        event.preventDefault();
        if (disabled) return;
        const number = parseQuestionCommand(typed, {requireWake:false});
        if (number === null) { setMessage('Enter a question number between 1 and 9999, such as 12.'); return; }
        const match = findNumberedQuestion(questions, number);
        if (match.error) { setMessage(match.error); return; }
        const opened = onOpen(match.question);
        setMessage(opened === false ? 'Stayed on this question.' : `Opened question ${number}.`);
    };
    return <><VoiceAssistantOverlay phase={phase} transcript={transcript} message={message} speaking={speaking} choices={choices} onCorrect={correctNumber} onDismiss={dismiss} onDisable={disableVoice}/><section className="iq-voice" aria-label="Voice question search">
        <div className="iq-voice-heading"><div><span className="iq-eyebrow">{awake ? 'I’M LISTENING' : listening ? 'READY FOR “HEY INSTIL”' : 'SAY IT. FIND IT.'}</span><h3>Hey Instil <span>✦</span></h3></div><button type="button" className={listening ? 'iq-secondary iq-listening' : 'iq-primary'} disabled={!supported || (disabled && !enabled)} aria-pressed={enabled} onClick={toggle}>{enabled ? 'Turn off hands-free' : 'Enable hands-free'}</button></div>
        {!supported && <p>Voice recognition isn’t supported in this browser. Type a question number below.</p>}
        <p role="status" aria-live="polite">{!supported && message.startsWith('Enable hands-free once') ? 'Enter the number shown on the question card.' : message}</p>
        {transcript && <p className="iq-voice-heard">Heard: “{transcript}”</p>}
        <form className="iq-voice-controls" onSubmit={openTyped}><label>Question number<input aria-label="Question number to open" placeholder="e.g. 12" inputMode="numeric" value={typed} onChange={event=>setTyped(event.target.value)} disabled={disabled}/></label><button className="iq-secondary" disabled={disabled || !typed.trim()} type="submit">Open question →</button><label>Speech language<select aria-label="Speech language" value={language} onChange={changeLanguage}><option value="en-US">English (US)</option><option value="en-GB">English (UK)</option><option value="en-IN">English (India)</option></select></label></form>
        <small>Enable once for this tab. Say “Hey Instil” and this subject’s displayed Q number, then pause. The command ends automatically; the microphone stays available for the next wake phrase while this IQ page is visible. Turn off hands-free to stop it. Your browser may send audio to its speech service, including while waiting for “Hey Instil”; Instil does not store recordings.</small>
    </section></>;
}
